import { useDebounce } from "@uidotdev/usehooks";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { format } from "date-fns";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { InstantSearch } from "react-instantsearch";
import {
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Dropdown,
  Form,
  Input,
} from "semantic-ui-react";
import DatePicker from "../components/datePicker/DatePicker";
import { CallView } from "../components/calls/CallView";
import CallRecordings from "../components/sidekick/CallRecordings";
import CallTranscriptions from "../components/sidekick/CallTranscriptions";
import CallTranscriptionSearchBox from "../components/sidekick/CallTranscriptionSearchBox";
import { useAuth } from "../contexts/AuthContext";
import { Btn, Card, Field, Page, PageHeader, Tabs } from "../components/ui";
import { useSearchOptions } from "../hooks/useSearchOptions";

const TABS = [
  { value: "search", label: "Search" },
  { value: "processed", label: "Processed" },
];

const style = {
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filter: { flex: "1 1 170px", minWidth: 0 },
};

function Sidekick() {
  const { apiUser } = useAuth();

  // Stable across route changes, so opening a call doesn't re-run the search
  const searchClient = useMemo(
    () => algoliasearch(apiUser.search_id, apiUser.search_key),
    [apiUser.search_id, apiUser.search_key],
  );

  return (
    <InstantSearch searchClient={searchClient} indexName="crts_index">
      <SidekickContent />
    </InstantSearch>
  );
}

// Nearest scrolling ancestor (the app shell's main column)
function scrollParent(el) {
  for (let n = el?.parentElement; n; n = n.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(n).overflowY)) return n;
  }
  return document.scrollingElement;
}

// /calls/:sqid shows the call in place of the lists. The lists stay mounted
// (hidden) so their tab, filters, page and search are as they were on Back.
function SidekickContent() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const openSqid = useMatch("/calls/:sqid")?.params.sqid;
  const [currentTab, setCurrentTab] = useState(
    openSqid ? "processed" : "search",
  );

  const openCall = (sqid) =>
    navigate(`/calls/${encodeURIComponent(sqid)}`, {
      state: { fromList: true },
    });
  const closeCall = () =>
    location.state?.fromList ? navigate(-1) : navigate("/calls");

  // Old ?call=SQID links
  const linkedCall = params.get("call");
  useEffect(() => {
    if (!linkedCall) return;
    setCurrentTab("processed");
    navigate(`/calls/${encodeURIComponent(linkedCall)}`, { replace: true });
  }, [linkedCall, navigate]);

  // Remember the list's scroll position while it's showing, start a call at
  // the top and put the list back where it was
  const rootRef = useRef(null);
  const listScroll = useRef(0);
  useLayoutEffect(() => {
    const el = scrollParent(rootRef.current);
    if (!el) return;
    if (openSqid) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = listScroll.current;
    const onScroll = () => (listScroll.current = el.scrollTop);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [openSqid]);

  const [searchCriteria, setSearchCriteria] = useState({});
  const [searchNumber, setSearchNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [extension, setExtension] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounce(userSearch, 400);
  const [searchOptions, searchOptionsIsLoading] =
    useSearchOptions(debouncedUserSearch);

  const [accordionIndex, setaccordionIndex] = useState(0);

  const handleAccordionClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = accordionIndex === index ? -1 : index;
    if (newIndex !== 1) {
      resetSearch();
    }
    setaccordionIndex(newIndex);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchCriteria({
      tel_number: searchNumber,
      from: startDate ? format(startDate, "yyyy-MM-dd HH:mm:ss") : undefined,
      to: endDate ? format(endDate, "yyyy-MM-dd HH:mm:ss") : undefined,
      ipecs_one_ext: extension,
    });
  };

  const resetSearch = () => {
    setSearchCriteria({});
    setSearchNumber("");
    setStartDate("");
    setEndDate("");
    setExtension("");
    setUserSearch("");
  };

  const hasCriteria = searchNumber || startDate || endDate || extension;

  // bare: no panel around it, for use inside the accordion
  const advancedSearched = ({ bare } = {}) => {
    const form = (
      <Form onSubmit={handleSearch} style={style.filters}>
        <Field label="Number" style={style.filter}>
          <Input
            placeholder="Search by number"
            value={searchNumber}
            name="tel_number"
            onChange={(e) => setSearchNumber(e.target.value)}
          />
        </Field>
        <Field label="Start time" style={style.filter}>
          <DatePicker
            placeholderText="Start time"
            dateFormat="dd/MM/yyyy HH:mm"
            selected={startDate}
            showTimeSelect
            onChange={(date) => setStartDate(date)}
          />
        </Field>
        <Field label="End time" style={style.filter}>
          <DatePicker
            placeholderText="End time"
            selected={endDate}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeSelect
            onChange={(date) => setEndDate(date)}
          />
        </Field>
        <Field label="Extension" style={style.filter}>
          <Dropdown
            placeholder="Extension"
            search
            selection
            fluid
            value={extension}
            loading={searchOptionsIsLoading}
            disabled={searchOptionsIsLoading}
            options={[
              { key: "all", value: "", text: "-" },
              ...(searchOptions.usersDropdownList || []),
            ]}
            onSearchChange={(_, { searchQuery }) => setUserSearch(searchQuery)}
            onChange={(_, { searchQuery, value }) => {
              setExtension(value);
              if (searchQuery === "") setUserSearch("");
            }}
            onClose={() => setUserSearch("")}
            onBlur={() => setUserSearch("")}
          />
        </Field>
        <div style={{ display: "flex", gap: "8px" }}>
          <Btn
            type="submit"
            variant="primary"
            icon="search"
            loading={isSearching}
            size="lg"
          >
            Search
          </Btn>
          {hasCriteria && (
            <Btn
              icon="close"
              aria-label="Clear search"
              title="Clear search"
              size="lg"
              onClick={resetSearch}
            />
          )}
        </div>
      </Form>
    );
    return bare ? (
      form
    ) : (
      <Card variant="panel" style={{ padding: "14px" }}>
        {form}
      </Card>
    );
  };

  return (
    <div ref={rootRef}>
      {openSqid && (
        <CallView key={openSqid} sqid={openSqid} onBack={closeCall} />
      )}
      <div hidden={!!openSqid}>
        <Page>
          <PageHeader
            title="iPECS calls"
            actions={
              <Tabs
                ariaLabel="iPECS calls"
                value={currentTab}
                onChange={setCurrentTab}
                items={TABS}
              />
            }
          />

          {currentTab === "search" ? (
            <>
              {advancedSearched()}
              <CallRecordings
                openCall={openCall}
                searchParams={searchCriteria}
                setIsSearching={setIsSearching}
                onViewProcessed={() => setCurrentTab("processed")}
              />
            </>
          ) : (
            <>
              <Accordion styled fluid>
                <AccordionTitle
                  active={accordionIndex === 0}
                  index={0}
                  onClick={handleAccordionClick}
                >
                  Simple search
                </AccordionTitle>
                <AccordionContent active={accordionIndex === 0}>
                  <CallTranscriptionSearchBox />
                </AccordionContent>

                <AccordionTitle
                  active={accordionIndex === 1}
                  index={1}
                  onClick={handleAccordionClick}
                >
                  Advanced search
                </AccordionTitle>
                <AccordionContent active={accordionIndex === 1}>
                  {advancedSearched({ bare: true })}
                </AccordionContent>
              </Accordion>

              <CallTranscriptions
                searchMode={accordionIndex === 0 ? "simple" : "advanced"}
                searchParams={searchCriteria}
              />
            </>
          )}
        </Page>
      </div>
    </div>
  );
}

export default Sidekick;
